import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, TrendingUp } from "lucide-react";

export default function IrlAdmin() {
  const navigate = useNavigate();
  const [indices, setIndices] = useState([]);
  const [leases, setLeases] = useState([]);
  const [np, setNp] = useState({ period: "", value: "" });
  const [revisingId, setRevisingId] = useState(null);
  const [rev, setRev] = useState({ new_period_value: "", reference_period_value: "", effective_date: "" });
  const [calcResult, setCalcResult] = useState(null);
  const activeLeases = useMemo(() => leases.filter(L => L.status === "active"), [leases]);

  const load = () => Promise.all([api.get("/admin/irl-indices"), api.get("/admin/leases")])
    .then(([a, b]) => { setIndices(a.data); setLeases(b.data); });
  useEffect(() => { load(); }, []);

  const addIndex = async () => {
    if (!np.period || !np.value) return;
    await api.put(`/admin/irl-indices/${np.period}`, { period: np.period, value: parseFloat(np.value) });
    setNp({ period: "", value: "" });
    toast.success("Indice enregistré");
    load();
  };

  const calcRevise = () => {
    const old_rent = leases.find(L => L.id === revisingId)?.rent_monthly || 0;
    const factor = parseFloat(rev.new_period_value) / parseFloat(rev.reference_period_value);
    const new_rent = (old_rent * factor).toFixed(2);
    setCalcResult({ old_rent, new_rent, factor: factor.toFixed(6) });
  };

  const applyRevise = async () => {
    try {
      await api.post(`/admin/leases/${revisingId}/revise`, {
        new_period_value: parseFloat(rev.new_period_value),
        reference_period_value: parseFloat(rev.reference_period_value),
        effective_date: rev.effective_date,
      });
      toast.success("Loyer révisé");
      setRevisingId(null); setRev({ new_period_value: "", reference_period_value: "", effective_date: "" }); setCalcResult(null);
      load();
    } catch (e) { toast.error("Échec"); }
  };

  const lease = leases.find(L => L.id === revisingId);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar /><Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</button>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">Révisions IRL</h1>
        <p className="text-sm opacity-70 mb-8">Indice de Référence des Loyers (INSEE) et révisions annuelles.</p>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-[color:var(--bg-surface)] border p-6" style={{ borderColor: "var(--border-default)" }}>
            <h2 className="font-display font-bold text-lg mb-4">Indices INSEE</h2>
            <div className="flex gap-2 mb-4">
              <input value={np.period} onChange={e => setNp({...np, period: e.target.value})} placeholder="2025-T4" className="input flex-1" data-testid="irl-period" />
              <input type="number" step="0.01" value={np.value} onChange={e => setNp({...np, value: e.target.value})} placeholder="146.51" className="input w-32" data-testid="irl-value" />
              <button onClick={addIndex} className="btn-brutal btn-brutal-solar px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="irl-add"><Plus className="w-4 h-4"/></button>
            </div>
            <div className="space-y-1 text-sm font-mono max-h-[300px] overflow-y-auto">
              {indices.map(i => <div key={i.period} className="flex justify-between border-b border-slate-900/10 py-2"><span>{i.period}</span><span className="font-bold">{i.value}</span></div>)}
            </div>
          </div>

          <div className="bg-[color:var(--bg-surface)] border p-6" style={{ borderColor: "var(--border-default)" }}>
            <h2 className="font-display font-bold text-lg mb-4">Réviser un bail</h2>
            <select value={revisingId || ""} onChange={e => { setRevisingId(e.target.value); setCalcResult(null); }} className="input mb-4" data-testid="revise-lease-select">
              <option value="">— Choisir un bail —</option>
              {activeLeases.map(L => <option key={L.id} value={L.id}>{L.tenant?.name} — {L.rent_monthly}€/mois</option>)}
            </select>
            {lease && (
              <div className="space-y-3">
                <div className="bg-slate-50 p-3 text-xs">
                  <div>Loyer actuel : <b>{lease.rent_monthly} €</b></div>
                  {lease.last_revision_date && <div>Dernière révision : {lease.last_revision_date}</div>}
                </div>
                <F label="Valeur de référence (IRL ancien)"><input type="number" step="0.01" value={rev.reference_period_value} onChange={e => setRev({...rev, reference_period_value: e.target.value})} className="input" data-testid="rev-ref" /></F>
                <F label="Nouvelle valeur (IRL nouveau)"><input type="number" step="0.01" value={rev.new_period_value} onChange={e => setRev({...rev, new_period_value: e.target.value})} className="input" data-testid="rev-new" /></F>
                <F label="Date d'effet"><input type="date" value={rev.effective_date} onChange={e => setRev({...rev, effective_date: e.target.value})} className="input" data-testid="rev-date" /></F>
                <button onClick={calcRevise} disabled={!rev.new_period_value || !rev.reference_period_value} className="btn-brutal bg-white border border-slate-900 w-full px-4 py-2 text-sm font-bold uppercase disabled:opacity-50 flex items-center justify-center gap-2"><TrendingUp className="w-4 h-4"/> Simuler</button>
                {calcResult && (
                  <div className="bg-[#FFC107]/20 border-2 border-[#FFC107] p-4 text-sm" data-testid="calc-result">
                    <div>Ancien loyer : <b>{calcResult.old_rent} €</b></div>
                    <div>Facteur : <b>×{calcResult.factor}</b></div>
                    <div className="font-display font-black text-xl mt-2">Nouveau loyer : {calcResult.new_rent} €</div>
                    <button onClick={applyRevise} disabled={!rev.effective_date} className="btn-brutal btn-brutal-solar mt-3 w-full px-4 py-2 text-sm font-bold uppercase disabled:opacity-50" data-testid="apply-rev">Appliquer cette révision</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>
    {children}
  </label>
);
